const eligibleCountries = new Set([
  'US', 'GB', 'AU', 'CA', 'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK',
  'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU',
  'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

export function shippingProgress({ total, count, requiresShipping, currency, storeCurrency, country, shopifyCurrency }) {
  if (!count || !requiresShipping || !eligibleCountries.has(country)) return { visible: false };
  if (!Number.isFinite(total) || total < 0) return { visible: false };
  let rate = currency === 'USD' ? 1 : null;
  if (currency !== 'USD' && storeCurrency === 'USD' && shopifyCurrency?.active === currency) {
    const candidate = Number(shopifyCurrency.rate);
    if (Number.isFinite(candidate) && candidate > 0) rate = candidate;
  }
  if (rate === null) return { visible: true, fallback: true };
  const threshold = Math.ceil(7000 * rate);
  const remaining = Math.max(0, threshold - total);
  return {
    visible: true,
    fallback: false,
    estimated: currency !== 'USD',
    reached: remaining === 0,
    remaining,
    percent: Math.min(100, total / threshold * 100),
  };
}

class SQShippingProgress extends HTMLElement {
  static observedAttributes = [
    'data-total', 'data-count', 'data-requires-shipping', 'data-currency',
    'data-store-currency', 'data-country', 'data-remaining', 'data-remaining-estimate',
    'data-reached', 'data-reached-estimate', 'data-fallback', 'data-note',
    'data-note-estimate', 'data-label',
  ];

  #queued = false;

  connectedCallback() {
    if (!this.shadowRoot) {
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `
        <style>
          :host { display: block; width: 100%; box-sizing: border-box; }
          :host([hidden]) { display: none; }
          .wrap { padding: 11px 12px; border-radius: 6px; background: #371517; color: #F5EEE6; font-family: inherit; }
          p { margin: 0; font-family: inherit; font-size: 13px; line-height: 1.5; }
          .track { height: 5px; margin: 8px 0 6px; overflow: hidden; border-radius: 99px; background: rgba(232,211,162,.22); }
          .fill { height: 100%; width: 0; border-radius: inherit; background: #E8D3A2; transition: width 180ms ease; }
          .note { color: #D3C2BC; font-size: 11px; line-height: 1.4; }
          [hidden] { display: none; }
          @media (prefers-reduced-motion: reduce) { .fill { transition: none; } }
          @media (forced-colors: active) { .track { border: 1px solid CanvasText; } .fill { background: Highlight; } }
        </style>
        <div class="wrap">
          <p class="message" role="status" aria-live="polite" aria-atomic="true"></p>
          <div class="track" role="progressbar" aria-valuemin="0" aria-valuemax="100"><div class="fill"></div></div>
          <p class="note"></p>
        </div>`;
    }
    this.#schedule();
  }

  attributeChangedCallback() { this.#schedule(); }

  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected && this.shadowRoot) this.#render();
    });
  }

  #render() {
    const d = this.dataset;
    const state = shippingProgress({
      total: Number(d.total),
      count: Number(d.count),
      requiresShipping: d.requiresShipping === 'true',
      currency: d.currency,
      storeCurrency: d.storeCurrency,
      country: d.country,
      shopifyCurrency: window.Shopify?.currency,
    });
    this.hidden = !state.visible;
    if (!state.visible) return;
    const message = this.shadowRoot.querySelector('.message');
    const track = this.shadowRoot.querySelector('.track');
    const fill = this.shadowRoot.querySelector('.fill');
    const note = this.shadowRoot.querySelector('.note');
    track.hidden = state.fallback;
    if (state.fallback) {
      message.textContent = d.fallback;
      note.textContent = d.note;
      return;
    }
    const amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: d.currency, currencyDisplay: 'symbol',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(state.remaining / 100) + ' ' + d.currency;
    const text = state.reached
      ? (state.estimated ? d.reachedEstimate : d.reached)
      : (state.estimated ? d.remainingEstimate : d.remaining);
    message.textContent = text.replace('[amount]', amount);
    fill.style.width = `${state.percent}%`;
    track.setAttribute('aria-label', d.label);
    track.setAttribute('aria-valuenow', String(Math.floor(state.percent)));
    track.setAttribute('aria-valuetext', message.textContent);
    note.textContent = state.estimated ? d.noteEstimate : d.note;
  }
}

if (!customElements.get('sq-shipping-progress')) {
  customElements.define('sq-shipping-progress', SQShippingProgress);
}
